import { all, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import {
  createPmFilter,
  deletePmFilter,
  fetchPmFilterById,
  fetchPmFilters,
  updatePmFilter,
} from 'PreventionPackage/MainModule/services/pmListFiltersApi';
import {
  getCatalogKnmStatuses248SelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
  makeGetCatalogSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  getDictionariesKnoOrganizationsSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
  makeGetDictionariesSubjectTypesMultiSelectOptions,
  makeGetDictionariesSubjectKindsMultiSelectOptions,
  makeGetDictionariesSubjectSubKindsMultiSelectOptions,
  getDictionariesAllProfKindsSelectOptions,
  getDictionariesRiskCategoriesSelectOptions,
  getDictionariesDangerClassesSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IProfKind } from 'APIPackage/DictionariesModule/interfaces/IProfKind';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { ICountrySelectOption } from 'CorePackage/CatalogsModule/interfaces/ICountrySelectOption';
import { IGenericReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IGenericReasonSelectOption';
import { IDictionaryTargetEntitySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryTargetEntitySelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import { IDictionaryRiskCategorySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryRiskCategorySelectOption';
import { IDictionaryDangerClassSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryDangerClassSelectOption';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { IPmListFilterRaw } from 'PreventionPackage/MainModule/interfaces/IPmListFilterRaw';
import {
  dictionariesProfKindsItemGet,
  dictionariesKnoOrganizationsItemGet,
  dictionariesSupervisionTypesItemGet,
  dictionariesTargetEntityItemGet,
  dictionariesSubjectTypesMultiItemGet,
  dictionariesSubjectKindsMultiItemGet,
  dictionariesSubjectSubKindsMultiItemGet,
  dictionariesRiskCategoriesItemGet,
} from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { dictionariesSupervisionTypesItemGetHandlerWithReturnOptions } from 'CorePackage/DictionariesModule/sagas/dictionariesSaga';

import { PmListFilterActionsEnum } from '../enums/PmListFilterActionsEnum';
import {
  pmListFilterCreate,
  pmListFilterGetData,
  pmListFilterGetMeta,
  pmListFilterRemove,
  pmListFilterSetCurrent,
  pmListFilterSetCurrentControlOrganizations,
  pmListFilterSetCurrentSupervisionTypes,
  pmListFilterSetData,
  pmListFilterSetMeta,
  pmListFilterUpdate,
} from '../actions/pmListFilterActions';
import { mapRawToPmFilter } from '../common/mapRawToPmFilter';
import { mapPmFilterToRaw } from '../common/mapPmFilterToRaw';
import { convertFilterDataToControlOrganizationsIds } from '../common/convertFilterDataToControlOrganizationsIds';
import { convertFilterDataToSupervisionTypesIds } from '../common/convertFilterDataToSupervisionTypesIds';
import { IPmListFilter } from '../interfaces/IPmListFilter';
import {
  getPmListCurrentControlOrganizations,
  getPmListCurrentSupervisionTypes,
} from '../selectors/pmListFilterSelectors';
import { ITerritorialUnitSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';

export function* watchPmListFilterSaga() {
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_INIT, pmListFilterInitSaga);
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_GET_META, pmListFilterGetMetaSaga);
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_GET_DATA, pmListFilterGetDataSaga);
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_CREATE, pmListFilterCreateSaga);
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_UPDATE, pmListFilterUpdateSaga);
  yield takeEvery(PmListFilterActionsEnum.PM_LIST_FILTER_REMOVE, pmListFilterRemoveSaga);
  yield takeEvery(
    PmListFilterActionsEnum.PM_LIST_FILTER_SET_CURRENT_CONTROL_ORGANIZATIONS,
    pmListFilterSetCurrentControlOrganizationsSaga,
  );
  yield takeEvery(
    PmListFilterActionsEnum.PM_LIST_FILTER_SET_CURRENT_SUPERVISION_TYPES,
    pmListFilterSetCurrentSupervisionTypesSaga,
  );
}

const pmListPageCatalogs: CommonCatalogsNamesEnum[] = [
  CommonCatalogsNamesEnum.KNM_YEARS,
  CommonCatalogsNamesEnum.SUPERVISION_TYPES,
  CommonCatalogsNamesEnum.USER_KNOS,
  CommonCatalogsNamesEnum.PM_STATUSES,
  CommonCatalogsNamesEnum.KNM_TYPES_CANCELED,
  CommonCatalogsNamesEnum.COUNTRIES,
  CommonCatalogsNamesEnum.GENERIC_REASONS,
  CommonCatalogsNamesEnum.TERRITORIAL_UNITS,
  CommonCatalogsNamesEnum.USER_KNOS,
];

function* pmListFilterInitSaga() {
  const state: IRootState = yield select();

  yield all(pmListPageCatalogs.map(catalogName => put(catalogGet(catalogName))));
  yield put(dictionariesProfKindsItemGet());
  yield put(dictionariesTargetEntityItemGet(undefined));
  yield put(dictionariesRiskCategoriesItemGet(undefined, undefined, undefined, undefined));
  yield put(dictionariesKnoOrganizationsItemGet());
  const controllingOrganizations = getPmListCurrentControlOrganizations(state);
  const supervisionTypes = getPmListCurrentSupervisionTypes(state);

  if (controllingOrganizations.length) {
    yield put(dictionariesSupervisionTypesItemGet(controllingOrganizations, false));
  }
  if (controllingOrganizations.length && supervisionTypes.length) {
    yield put(dictionariesSubjectTypesMultiItemGet(controllingOrganizations, supervisionTypes));
    yield put(dictionariesSubjectKindsMultiItemGet(controllingOrganizations, supervisionTypes));
    yield put(dictionariesSubjectSubKindsMultiItemGet(controllingOrganizations, supervisionTypes));
  }
  yield put(pmListFilterGetMeta());
}

function* pmListFilterGetMetaSaga() {
  function* funcTry() {
    const meta: IFilterResult[] = yield fetchPmFilters();

    yield put(pmListFilterSetMeta(meta));
    const current = getStorageItem<IPmListFilter>(StorageNamesEnum.PM_FILTER);

    if (current?.controllingOrganizations) {
      const ids: string[] = current?.controllingOrganizations
        .map(item => (typeof item.value !== 'string' ? item.value.dictVersionId : ''))
        .filter(item => item);

      yield put(pmListFilterSetCurrentControlOrganizations(ids));
    }

    if (current?.supervisionType) {
      yield put(pmListFilterSetCurrentSupervisionTypes([current.supervisionType.value.dictVersionId]));
    }

    const currentId = meta.find(m => m.name === current?.name)?.id;

    yield put(pmListFilterSetCurrent({ current, currentId }));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* pmListFilterGetDataSaga(action: ActionType<typeof pmListFilterGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const erknmOrganisationsSelectOptions:
      | IDictionaryKnoOrganizationSelectOption[]
      | undefined = getDictionariesKnoOrganizationsSelectOptions(state);
    const supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] = [];
    const pmStatusesSelectOptions: IKnmStatusSelectOption[] = getCatalogKnmStatuses248SelectOptions(state);
    const getDictionariesCountriesSelectOptions = makeGetCatalogSelectOptions(CommonCatalogsNamesEnum.COUNTRIES);
    const countriesSelectOptions: ICountrySelectOption[] | undefined = getDictionariesCountriesSelectOptions(state);
    const getDictionariesGenericReasonsSelectOptions = makeGetCatalogSelectOptions(
      CommonCatalogsNamesEnum.GENERIC_REASONS,
    );
    const reasonsSelectOptions: IGenericReasonSelectOption[] | undefined = getDictionariesGenericReasonsSelectOptions(
      state,
    );
    const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();
    const targetEntitiesSelectOptions:
      | IDictionaryTargetEntitySelectOption[]
      | undefined = getDictionariesTargetEntitySelectOptions(state, undefined)?.filter(
      targetEntityOption => targetEntityOption.code !== null,
    );
    const getDictionariesSubjectTypesMultiSelectOptions = makeGetDictionariesSubjectTypesMultiSelectOptions();
    const getDictionariesSubjectKindsMultiSelectOptions = makeGetDictionariesSubjectKindsMultiSelectOptions();
    const getDictionariesSubjectSubKindsMultiSelectOptions = makeGetDictionariesSubjectSubKindsMultiSelectOptions();
    const subjectTypesSelectOptions:
      | IDictionarySubjectTypeSelectOption[]
      | undefined = getDictionariesSubjectTypesMultiSelectOptions(state, [], []);
    const subjectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined = [];
    const subjectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined = [];
    const riskCategoriesSelectOptions:
      | IDictionaryRiskCategorySelectOption[]
      | undefined = getDictionariesRiskCategoriesSelectOptions(state, undefined);
    const dangerClassesSelectOptions:
      | IDictionaryDangerClassSelectOption[]
      | undefined = getDictionariesDangerClassesSelectOptions(state, undefined);

    const kindsSelectOptions:
      | IDictionarySelectOption<IProfKind>[]
      | undefined = getDictionariesAllProfKindsSelectOptions(state);
    const { filters } = state.pmListFilter;

    if (filters.find(f => f.meta.id === id)?.data !== undefined) {
      return;
    }
    const territorialUnitsSelectOptions: ITerritorialUnitSelectOption[] = getCatalogsTerritorialUnitsSelectOptions(
      state,
    );
    const raw: IPmListFilterRaw = yield fetchPmFilterById(id);
    let filter = mapRawToPmFilter({
      raw,
      organisationsSelectOptions: erknmOrganisationsSelectOptions || [],
      supervisionTypesSelectOptions,
      pmStatusesSelectOptions,
      countriesSelectOptions,
      reasonsSelectOptions,
      targetEntitiesSelectOptions,
      subjectTypesSelectOptions,
      subjectKindsSelectOptions,
      subjectSubKindsSelectOptions,
      riskCategoriesSelectOptions,
      dangerClassesSelectOptions,
      kindsSelectOptions,
      territorialUnitsSelectOptions,
    });

    const ids = convertFilterDataToControlOrganizationsIds(filter.controllingOrganizations);
    const stids = convertFilterDataToSupervisionTypesIds(filter.supervisionType ? [filter.supervisionType] : []);

    const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
    let options: IDictionarySupervisionTypeSelectOption[] | undefined = getDictionariesSupervisionTypesSelectOptions(
      state,
      ids || [],
      false,
      false,
    );

    if (ids?.length) {
      if (!options?.length) {
        options = yield dictionariesSupervisionTypesItemGetHandlerWithReturnOptions(ids);
      }

      filter = {
        ...filter,
        supervisionType: options?.find(
          (supervisionTypeSelectOption: IDictionarySupervisionTypeSelectOption) =>
            supervisionTypeSelectOption?.id === raw?.supervisionTypeId,
        ),
      };

      const stoptions: IDictionarySubjectTypeSelectOption[] | undefined = getDictionariesSubjectTypesMultiSelectOptions(
        state,
        ids || [],
        stids || [],
      );
      const skoptions: IDictionarySubjectKindSelectOption[] | undefined = getDictionariesSubjectKindsMultiSelectOptions(
        state,
        ids || [],
        stids || [],
      );
      const sskoptions:
        | IDictionarySubjectSubKindSelectOption[]
        | undefined = getDictionariesSubjectSubKindsMultiSelectOptions(state, ids || [], stids || []);

      if (stids?.length) {
        filter = {
          ...filter,
          subjectTypes: stoptions?.filter((subjectTypeSelectOption: IDictionarySubjectTypeSelectOption) =>
            raw?.subjectTypeIds?.find(subjectTypeId => subjectTypeId === subjectTypeSelectOption?.id),
          ),
          objectKinds: skoptions?.filter((subjectKindSelectOption: IDictionarySubjectKindSelectOption) =>
            raw?.objectKindIds?.find(subjectKindId => subjectKindId === subjectKindSelectOption?.id),
          ),
          objectSubKinds: sskoptions?.filter((subjectSubKindSelectOption: IDictionarySubjectSubKindSelectOption) =>
            raw?.objectSubKindIds?.find(subjectSubKindId => subjectSubKindId === subjectSubKindSelectOption?.id),
          ),
        };
      }
    }

    yield put(pmListFilterSetCurrentControlOrganizations(ids || []));
    yield put(pmListFilterSetCurrentSupervisionTypes(stids || []));
    yield put(pmListFilterSetData(id, filter));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* pmListFilterCreateSaga(action: ActionType<typeof pmListFilterCreate>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const rawFilter = mapPmFilterToRaw(data);
    const meta: IFilterResult = yield createPmFilter(rawFilter);
    const metaList = state.pmListFilter.filters.map(f => f.meta);

    yield put(pmListFilterSetMeta([...metaList, meta]));
    yield put(pmListFilterSetData(meta.id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* pmListFilterUpdateSaga(action: ActionType<typeof pmListFilterUpdate>) {
  function* funcTry() {
    const { id, data } = action.payload;
    const rawFilter = mapPmFilterToRaw(data);

    yield updatePmFilter(id, rawFilter);
    yield put(pmListFilterSetData(id, data));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* pmListFilterRemoveSaga(action: ActionType<typeof pmListFilterRemove>) {
  yield sagaWrapper({
    *funcTry() {
      const { id } = action.payload;

      yield deletePmFilter(id);
    },
  });
}

function* pmListFilterSetCurrentControlOrganizationsSaga(
  action: ActionType<typeof pmListFilterSetCurrentControlOrganizations>,
) {
  const { ids } = action.payload;

  if (ids.length) {
    yield put(dictionariesSupervisionTypesItemGet(ids, false));

    const state: IRootState = yield select();
    const supervisionTypes = getPmListCurrentSupervisionTypes(state);

    if (supervisionTypes.length) {
      yield put(dictionariesSubjectTypesMultiItemGet(ids, supervisionTypes));
      yield put(dictionariesSubjectKindsMultiItemGet(ids, supervisionTypes));
      yield put(dictionariesSubjectSubKindsMultiItemGet(ids, supervisionTypes));
    }
  }
}

function* pmListFilterSetCurrentSupervisionTypesSaga(
  action: ActionType<typeof pmListFilterSetCurrentSupervisionTypes>,
) {
  const { ids } = action.payload;

  const state: IRootState = yield select();
  const controllingOrganizations = getPmListCurrentControlOrganizations(state);

  if (controllingOrganizations.length && ids.length) {
    yield put(dictionariesSubjectTypesMultiItemGet(controllingOrganizations, ids));
    yield put(dictionariesSubjectKindsMultiItemGet(controllingOrganizations, ids));
    yield put(dictionariesSubjectSubKindsMultiItemGet(controllingOrganizations, ids));
  }
}
